import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createClientOrganization(
    $data: createClientOrganizationInput!
  ) {
    createClientOrganization(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createClientOrganizationRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateClientOrganizationInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'organization/create_client_organization',
  async (
  params: {data: TYPES.CreateClientOrganizationInput;},
  { extra: client }) =>
  {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createClientOrganization;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createClientOrganizationSlice = createSlice({
  name: 'create_client_organization',
  initialState: initialState,
  reducers: {
    createClientOrganizationClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createClientOrganizationRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      createClientOrganizationRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(createClientOrganizationRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createClientOrganizationClear } =
createClientOrganizationSlice.actions;

export const createClientOrganization = createSelector(
  (state: RootState) => state.organization.createClientOrganization.result,
  (state: RootState) => state.organization.createClientOrganization.error,
  (state: RootState) => state.organization.createClientOrganization.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createClientOrganizationSlice.reducer;